// Checkout Manager Class for the Checkout Success Section
// Handles business logic and state management for checkout success page

import { Manager } from '../../../core';
import { logger } from '../../../core';
import { navigationManager } from '../../../managers/navigation-manager';

export class CheckoutManager extends Manager {
  constructor() {
    super();
    this.sessionData = null;
    this.isLoading = true;
    this.isInitialized = false;
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    logger.log(
      {
        tags: "manager|checkout|init",
        color1: "cyan",
      },
      "Initializing CheckoutManager"
    );
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log(
      {
        tags: "manager|checkout|ready",
        color1: "lime",
      },
      "CheckoutManager states initialized"
    );

    this.isInitialized = true;
    this.isLoading = false;
  }

  /**
   * Set session data from URL params, Stripe session, or payload
   */
  setSessionData(data) {
    this.sessionData = data;
    this.isLoading = false;
  }

  /**
   * Fetch checkout session details from backend
   * This will also trigger transaction update if session is completed
   */
  async fetchSessionDetails(sessionId) {
    try {
      this.isLoading = true;

      const response = await fetch(`/api/stripe/checkout-session/${sessionId}`, {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to fetch session details');
      }

      // Extract plan details from session metadata
      const session = data.session;
      const metadata = session.metadata || {};

      this.setSessionData({
        sessionId: session.id,
        planId: metadata.planId,
        tierId: metadata.tierId,
        billingCycle: metadata.billingCycle,
        price: session.amount_total ? (session.amount_total / 100).toFixed(2) : null,
        paymentStatus: session.payment_status,
        status: session.status,
        customer: session.customer,
        paymentIntent: session.payment_intent,
        subscription: session.subscription,
      });

      logger.log({
        tags: "manager|checkout|session",
        color: "lime"
      }, `Fetched session details for ${sessionId}`);

      return session;
    } catch (error) {
      logger.error({
        tags: "manager|checkout|error",
        color1: "red"
      }, "Failed to fetch session details:", error);
      this.isLoading = false;
      throw error;
    }
  }

  /**
   * Handle incoming payload from pricing section
   */
  handlePayload(payload) {
    if (!payload || !payload.action) return;

    logger.log({
      tags: "manager|checkout|payload",
      color: "yellow"
    }, `Received payload: ${payload.action}`);

    if (payload.action === 'checkout_success') {
      // Set session data from payload
      this.setSessionData({
        planId: payload.planId,
        tierId: payload.tierId,
        billingCycle: payload.billingCycle,
        price: payload.price,
      });
    }
  }

  /**
   * Navigate to account section
   */
  navigateToAccount() {
    logger.log(
      {
        tags: "manager|checkout|navigation",
        color: "cyan",
      },
      "Navigating to account section"
    );

    navigationManager.navigateTo("account");
  }

  /**
   * Navigate to pricing section
   */
  navigateToPricing() {
    logger.log(
      {
        tags: "manager|checkout|navigation",
        color: "cyan",
      },
      "Navigating to pricing section"
    );

    navigationManager.navigateTo("pricing");
  }

  /**
   * Get session data
   */
  getSessionData() {
    return this.sessionData;
  }

  /**
   * Get the current initialization status
   */
  getIsInitialized() {
    return this.isInitialized;
  }
}
